import {useEffect, useMemo, useRef, useState} from 'react';
import {errorMessageKey} from '../../api';
import type {
  ChannelMenuCategory,
  ChannelMenuItem,
  ItemAvailability,
  LocalizedText,
} from '../../api/types';
import {ROUTES, href} from '../../app/routes';
import {Link} from '../../app/router';
import {useBranding} from '../../branding';
import {useCart} from '../../cart';
import {useI18n} from '../../i18n';
import {useMenu} from '../../menu';
import {useOrder} from '../../order';
import {useGuestSession} from '../../session';
import {useStreamEvents} from '../../stream';
import {
  ActionLink,
  Button,
  Card,
  ChipRow,
  Chip,
  Money,
  Photo,
  Screen,
  ScreenBody,
  ScreenFooter,
  SkeletonBar,
  SkeletonRow,
  StatusPill,
  StickyBlock,
  TopBar,
  VenueLabel,
} from '../../ui';
import {LanguageToggle} from '../Q02LanguageToggle';
import './q03.css';

/**
 * # Q3 · Menu browse — route `/menu`
 *
 * The screen to optimise above everything: it is the one a guest spends the most
 * time on, on the worst phone, on the worst connection.
 *
 * ## The shell, and why this screen is the one that scrolls itself
 *
 * Every other footerless screen lets the document scroll, because a document
 * scroll collapses the browser's URL bar and an inner one does not. This screen
 * takes the other trade on purpose: the search and category row are the only way
 * back out of a ninety-item menu, so they are pinned with `StickyBlock` inside a
 * `ScreenBody scroll`, and the cart bar is pinned by flex underneath it. Both
 * need the body to be its own scroll region.
 *
 * The title block scrolls away with the list, which the design prototype's
 * doubly-nested scrollers hid: there, `เมนู` and the search block both sat
 * outside the scrolling list and ate 120px of a 640px viewport permanently. One
 * scroll region means the guest gets that height back and keeps the control that
 * matters — the venue name in the top bar never moves either way.
 *
 * ## The no-photo layout is the layout
 *
 * `image_url` is null for most V1 merchants and photo sourcing is an unresolved
 * TBD (03 §constraints, foundations §14). So the row is designed as type and
 * price — name left, price right, nothing else load-bearing — and a photo is a
 * 56px thumbnail that *joins* an already-complete row. `Photo` renders **nothing
 * at all** for a null `image_url`: no grey placeholder box, no "no photo"
 * caption, no reserved space and no second layout to maintain. The design
 * package draws a striped placeholder in that slot, and ninety striped boxes
 * read as a page that failed to load; the stripe survives only as the background
 * behind a real photo while it decodes. `test/guest-path.test.tsx` asserts zero
 * `img` elements on a menu with no photography, which is the normal case.
 *
 * ## Sold out is data, not an error
 *
 * Two different things happen and both are visible:
 *
 *   * An item that is already unavailable stays in the list, labelled
 *     **หมดแล้ว** in its price slot, in place of the number (owner,
 *     2026-08-26). Removing it would make a guest hunt for a dish that is on
 *     the printed menu and conclude the page is broken.
 *   * An item that goes unavailable **while the guest is looking at it** raises a
 *     notice (`q3.itemJustSoldOut`) in a polite live region. Two sources feed it:
 *     an 86 arriving over the stream, and an item disappearing from the
 *     projection entirely on a `menu.updated` re-read. A dish that vanishes
 *     silently is how a guest ends up ordering something they did not choose.
 *
 * Sold-out rows are not links. Q4 handles the 86'd-while-open case, but there is
 * no reason to walk a guest into it from here.
 *
 * The dimming is on the **name, price and photo only**, not on the row. The
 * design package fades the whole row to 50%, which fades the one thing on it
 * that has to survive a bright afternoon and a scratched screen protector: the
 * Palm Sugar หมดแล้ว pill that says *why* the row is inert. Colour is never the
 * only carrier of meaning (foundations §12), so the label keeps its contrast and
 * the type around it recedes instead.
 *
 * ## What is deliberately absent
 *
 * *Dietary and spice markers.* 03 §Q3 asks for them and `q3.marker*` exists in
 * all three dictionaries, but `ChannelMenuItem` in the frozen contract carries no
 * diet, allergen or spice field (ADR-0005 — the contract is not ours to change).
 * Inventing one would be inventing an allergen claim, which is the worst possible
 * thing on this surface to guess at. Nothing renders until a field lands.
 *
 * ## Money
 *
 * Every figure here is a menu price: `MoneyTone.neutral`, and **there is no green
 * anywhere on this screen** — not on the price, not on the cart bar, not on a
 * badge. Every figure goes through `<Money>` rather than a formatted string, so
 * the tabular figures, the `฿125`-never-`฿125.00` rule and the money-colour
 * decision are all made in one place instead of thirteen.
 *
 * Per-channel pricing means the QR price may legitimately differ from the printed
 * menu, so `q3.channelPriceNote` states it once, quietly, before the first price —
 * a difference that is explained in advance is not read as an error.
 */

/** At most this many "just sold out" notices at once — newest first. */
const MAX_NOTICES = 3;

interface SoldOutNotice {
  itemId: string;
  /** The snapshot, not a rendered string: switching language must re-read it. */
  name: LocalizedText;
}

/** `sort_order` is optional; `Array.prototype.sort` is stable, so ties keep projection order. */
function bySortOrder<T extends {sort_order?: number}>(list: readonly T[]): T[] {
  return [...list].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
}

/**
 * One lowercase haystack per item, across **all three languages**.
 *
 * A tourist reading the English menu may still type a Thai dish name they heard,
 * and a Thai guest on a Chinese phone may type either. Matching only the rendered
 * language would make search worse in exactly the venue that needs it most.
 */
function haystack(item: ChannelMenuItem): string {
  const parts = [
    item.name.th,
    item.name.en,
    item.name.zh_hans,
    item.description?.th,
    item.description?.en,
    item.description?.zh_hans,
  ];
  return parts.filter(Boolean).join(' ').toLowerCase();
}

export default function Q03MenuBrowse() {
  const {t, ln} = useI18n();
  const {status, categories, itemsById, error, reload, availabilityOf} = useMenu();
  const {status: sessionStatus, session} = useGuestSession();
  const {count, estimatedSubtotalSatang} = useCart();
  const {knownOrderIds} = useOrder();

  const [query, setQuery] = useState('');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [notices, setNotices] = useState<SoldOutNotice[]>([]);

  const pushNotice = (notice: SoldOutNotice) =>
    setNotices((current) => [
      notice,
      ...current.filter((n) => n.itemId !== notice.itemId),
    ].slice(0, MAX_NOTICES));

  // An 86 from the KDS, live. `useStreamEvents` keeps the latest closure, so
  // `itemsById` here is current.
  useStreamEvents((event) => {
    if (event.type !== 'menu.availability_changed') return;
    const update = event.availability;
    if (!update || update.available) return;
    const item = itemsById.get(update.item_id);
    if (item) pushNotice({itemId: update.item_id, name: item.name});
  });

  // An item that leaves the projection altogether — a `menu.updated` re-read that
  // dropped it. The stream event above never fires for this, and without it the
  // dish simply stops existing under the guest's thumb.
  const previousItems = useRef<Map<string, LocalizedText> | null>(null);
  useEffect(() => {
    const current = new Map<string, LocalizedText>();
    for (const [id, item] of itemsById) current.set(id, item.name);
    const before = previousItems.current;
    previousItems.current = current;
    // Nothing to compare against on the first menu, and an empty map during a
    // failed reload is not evidence that anything sold out.
    if (!before || before.size === 0 || current.size === 0) return;
    for (const [id, name] of before) {
      if (!current.has(id)) pushNotice({itemId: id, name});
    }
  }, [itemsById]);

  const sortedCategories = useMemo(
    () => bySortOrder(categories).map((c) => ({...c, items: bySortOrder(c.items)})),
    [categories],
  );

  // A category can disappear on a menu update while it is selected; falling back
  // to "all" beats an empty screen with a chip pointing at nothing.
  const activeCategoryId =
    categoryId && sortedCategories.some((c) => c.category_id === categoryId)
      ? categoryId
      : null;

  const needle = query.trim().toLowerCase();

  const sections: ChannelMenuCategory[] = useMemo(() => {
    if (needle) {
      // Search is menu-wide on purpose: a guest who types a dish name wants the
      // dish, not the dish within the category they happen to have tapped.
      return sortedCategories
        .map((c) => ({...c, items: c.items.filter((i) => haystack(i).includes(needle))}))
        .filter((c) => c.items.length > 0);
    }
    if (activeCategoryId) {
      return sortedCategories.filter((c) => c.category_id === activeCategoryId);
    }
    return sortedCategories;
  }, [sortedCategories, needle, activeCategoryId]);

  /**
   * The `featured` spotlight, flattened across every category and de-duplicated —
   * a merchant could flag the same dish in two places, and it belongs in the band
   * once. A featured item is *also* drawn in its own category below: the flag adds
   * a spotlight, it does not move the dish. No cap, in projection order.
   */
  const featuredItems = useMemo(() => {
    const seen = new Set<string>();
    const out: ChannelMenuItem[] = [];
    for (const category of sortedCategories) {
      for (const item of category.items) {
        if (item.featured && !seen.has(item.item_id)) {
          seen.add(item.item_id);
          out.push(item);
        }
      }
    }
    return out;
  }, [sortedCategories]);

  const resultCount = sections.reduce((sum, c) => sum + c.items.length, 0);
  const menuIsEmpty = status === 'ready' && sortedCategories.length === 0;
  const hasMenu = sortedCategories.length > 0;
  const sessionGone = sessionStatus === 'expired' || sessionStatus === 'invalid';

  // The spotlight belongs to the default browse view only. A guest who has typed
  // a search or narrowed to one category has said what they want, and a band of
  // "featured" dishes on top of that answer is noise. Zero featured items renders
  // nothing at all — there is no empty band.
  const showHero = hasMenu && !needle && !activeCategoryId && featuredItems.length > 0;

  /**
   * An order this table has already sent — either one this phone submitted a
   * minute ago (`knownOrderIds`, no network) or one the session came back
   * carrying, which is how a second phone at the same table sees it.
   */
  const hasSentOrder = Boolean(session?.active_order_id) || knownOrderIds.length > 0;

  const selectCategory = (id: string | null) => {
    setCategoryId(id);
    // The no-results copy offers a category as the way out of a bad search, so
    // taking that way out has to actually clear the search.
    setQuery('');
  };

  return (
    <Screen>
      <a className="q3-skip" href="#q3-items">
        {t('common.skipToContent')}
      </a>

      {/*
        The venue name occupies the slot the back link has on every other screen:
        the menu is the top of this flow and there is nowhere behind it. The
        language control is the trailing slot on every screen that has a top bar
        — `test/guest-path.test.tsx` walks nine routes and requires it.
      */}
      {/*
        The way to the kitchen's copy of the order lives in the top bar, before
        the language control (owner directive, 2026-08-14). It used to be a
        full-width outline button in the footer, shown only when the cart was
        empty — so a guest halfway through a second round could not reach the
        status of the first without emptying their cart first. Up here it is
        reachable in every cart state, and it stops competing with the send
        action for the one primary slot the red rule allows.

        Order within the trailing slot is the directive's, and it is also the
        right way round: `.pk-topbar-trailing` is a flex row reading left to
        right, so the language control stays in the far corner — the fixed
        landmark on all thirteen screens — and the status link sits inboard of
        it, appearing and disappearing without ever moving it.
      */}
      <TopBar
        leading={<VenueName />}
        trailing={
          <>
            {hasSentOrder ? (
              <ActionLink to={ROUTES.status} variant="text" className="q3-status-link">
                {t('q7.viewStatus')}
              </ActionLink>
            ) : null}
            <LanguageToggle variant="link" />
          </>
        }
      />

      <ScreenBody scroll>
        <div className="q3-intro">
          <h1 className="pk-h1">{t('q3.title')}</h1>
          <MenuNote hasMenu={hasMenu} tableLabel={session?.table_label ?? null} />
        </div>

        {hasMenu ? (
          <StickyBlock className="q3-sticky">
            <div className="q3-search">
              <label className="pokpok-sr-only" htmlFor="q3-search-input">
                {t('q3.searchLabel')}
              </label>
              <input
                id="q3-search-input"
                className="pk-input q3-search-input"
                type="search"
                inputMode="search"
                autoComplete="off"
                enterKeyHint="search"
                placeholder={t('q3.searchPlaceholder')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {/*
                An addition: the prototype's field has no clear control. It
                stays because neither mobile Thai keyboard repeats backspace on
                press-and-hold, so undoing a search by hand is a dozen taps —
                and the way out of a bad search must not cost more than the
                search did. It is the glyph alone, with `q3.searchClear` as the
                accessible name rather than a visible 16px phrase; `q03.css`
                carries the measurement and the reason there is no underline.
              */}
              {query ? (
                <Button
                  variant="text"
                  className="q3-search-clear"
                  aria-label={t('q3.searchClear')}
                  onClick={() => setQuery('')}
                >
                  <span aria-hidden="true">✕</span>
                </Button>
              ) : null}
            </div>

            <ChipRow label={t('q3.title')}>
              <Chip selected={activeCategoryId === null} onSelect={() => selectCategory(null)}>
                {t('q3.allCategories')}
              </Chip>
              {sortedCategories.map((category) => (
                <Chip
                  key={category.category_id}
                  selected={activeCategoryId === category.category_id}
                  onSelect={() => selectCategory(category.category_id)}
                >
                  {ln(category.name)}
                </Chip>
              ))}
            </ChipRow>
          </StickyBlock>
        ) : null}

        {/*
         * Polite, not assertive: a dish selling out is news, not an alarm, and an
         * assertive region would cut across a guest mid-sentence.
         */}
        <div className="q3-notices" role="status" aria-live="polite">
          {notices.map((notice) => (
            <Card key={notice.itemId} tone="warning" padding="tight" className="q3-notice">
              <span className="q3-notice-text">
                {t('q3.itemJustSoldOut', {item: ln(notice.name)})}
              </span>
              <Button
                variant="text"
                className="q3-notice-close"
                onClick={() =>
                  setNotices((current) => current.filter((n) => n.itemId !== notice.itemId))
                }
              >
                {t('common.close')}
              </Button>
            </Card>
          ))}
        </div>

        <div id="q3-items" className="q3-items" tabIndex={-1}>
          {showHero ? (
            <HeroBand items={featuredItems} availabilityOf={availabilityOf} />
          ) : null}

          {!hasMenu && (status === 'loading' || status === 'idle') && !sessionGone ? (
            <MenuSkeleton label={t('q3.loading')} />
          ) : null}

          {!hasMenu && (status === 'error' || sessionGone) ? (
            <Card as="section" className="q3-panel">
              <h2 className="q3-panel-title">{t('q3.menuUnavailable')}</h2>
              <p className="pk-muted">
                {t(sessionGone ? 'error.session' : errorMessageKey(error))}
              </p>
              <p className="pk-muted">{t('q3.menuUnavailableAction')}</p>
              {/*
                Outline, not the solid red. The cart bar below is this screen's
                one primary action and a guest with items in the cart can be
                looking at a failed reload at the same time — two solid reds on
                one screen tells them nothing (foundations §6).
              */}
              {!sessionGone ? (
                <Button variant="secondary" block className="q3-panel-action" onClick={reload}>
                  {t('common.retry')}
                </Button>
              ) : null}
            </Card>
          ) : null}

          {menuIsEmpty ? (
            <Card as="section" className="q3-panel">
              <p className="pk-muted">{t('q3.emptyCategory')}</p>
              <p className="pk-muted">{t('common.askStaff')}</p>
            </Card>
          ) : null}

          {hasMenu && resultCount === 0 && needle ? (
            <Card as="section" className="q3-panel">
              <h2 className="q3-panel-title">{t('q3.searchNoResults', {query: query.trim()})}</h2>
              <p className="pk-muted">{t('q3.searchNoResultsAction')}</p>
            </Card>
          ) : null}

          {hasMenu && resultCount === 0 && !needle ? (
            <Card as="section" className="q3-panel">
              <p className="pk-muted">{t('q3.emptyCategory')}</p>
            </Card>
          ) : null}

          {sections.map((category) => (
            <section key={category.category_id} className="q3-section">
              <h2 className="q3-section-title">{ln(category.name)}</h2>
              <ul className="q3-list">
                {category.items.map((item) => {
                  const availability = availabilityOf(item.item_id);
                  const available = availability?.available ?? false;
                  return (
                    <li key={item.item_id}>
                      <MenuRow
                        item={item}
                        available={available}
                        name={ln(item.name)}
                        soldOutLabel={t('common.soldOut')}
                      />
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}

          {/*
            The attribution line is an addition here: the prototype prints
            `สั่งอาหารด้วย pokpokPOS` on the success, status and payment screens
            and never on the menu. `Screen.tsx` makes it a surface-wide agreement
            — every screen draws its own, and they all draw it the same — and a
            convention beats a per-screen inconsistency in the package,
            especially on the one screen a guest spends the most time on. It is
            `pk-hint` at the foot of the list, below the last dish and above
            nothing: the venue leads and pokpok stays subordinate (03 §Q1).

            Only when there is a menu. On the error and loading states the line
            would be the sole thing on the screen with a brand in it, which is
            the one place it must not be.
          */}
          {hasMenu ? <p className="pk-hint q3-attribution">{t('common.attribution')}</p> : null}
        </div>
      </ScreenBody>

      {/*
        The bottom bar now has exactly one job and one look: solid red to the
        order list while there are unsent items, and absent otherwise. The
        outline-to-status branch that used to live here moved into the top bar,
        which also removed the reason this was a two-branch decision — the red
        rule is still stated as a layout, it just has less to say.
      */}
      {count > 0 ? (
        <ScreenFooter>
          <ActionLink to={ROUTES.cart} variant="primary" className="q3-cart-action">
            {t('q3.viewCartWithCount', {count})}
            <Money satang={estimatedSubtotalSatang} />
          </ActionLink>
        </ScreenFooter>
      ) : null}
    </Screen>
  );
}

/**
 * One menu row.
 *
 * The available row is a link and the sold-out row is not — the same markup with
 * `aria-disabled` would still be focusable and still look tappable, and a guest
 * who taps a dish that cannot be ordered has been told "no" twice.
 */
function MenuRow({
  item,
  available,
  name,
  soldOutLabel,
}: {
  item: ChannelMenuItem;
  available: boolean;
  name: string;
  soldOutLabel: string;
}) {
  const body = (
    <>
      {/* Nothing at all when `image_url` is null, which is most V1 merchants. */}
      <Photo src={item.image_url} shape="thumb" className="q3-row-photo" />
      <span className="q3-row-text">
        <span className="q3-row-name">{name}</span>
      </span>
      {/*
        Sold out replaces the price outright with หมดแล้ว, and the kitchen's
        reason ("ครัวแจ้งหมด") is dropped (owner, 2026-08-26): the price slot is
        where a guest already looks for whether a dish is orderable, so one word
        there says everything the pill-plus-reason stack used to, in the place
        the eye is already resting.
      */}
      {available ? (
        <>
          {item.regular_price_satang != null && (
            <Money satang={item.regular_price_satang} className="q3-row-was" />
          )}
          <Money satang={item.price_satang} className="q3-row-price" />
        </>
      ) : (
        <span className="q3-row-price q3-row-soldout">{soldOutLabel}</span>
      )}
    </>
  );

  if (!available) return <div className="q3-row is-soldout">{body}</div>;

  return (
    <Link className="q3-row pokpok-tap" to={href(ROUTES.item, {itemId: item.item_id})}>
      {body}
    </Link>
  );
}

/**
 * The venue leads where it is known; pokpok stays subordinate (03 §Q1).
 *
 * When branding carries a logo (2026-08-21) the mark stands in for the name in
 * this slot, with the name still its accessible label; with no logo it is the
 * name text exactly as before. The name is the fallback and it is never lost.
 */
function VenueName() {
  const {ln} = useI18n();
  const {session} = useGuestSession();
  const {logoUrl} = useBranding();
  const name = session?.merchant_display_name ? ln(session.merchant_display_name) : '';
  if (!name && !logoUrl) return null;
  return <VenueLabel name={name} logoUrl={logoUrl} />;
}

/**
 * The featured spotlight — a horizontal band of the dishes the merchant flagged,
 * above the ordinary menu. Rendered only by the caller's `showHero`, so this
 * component never has to reason about an empty list or a search in progress.
 *
 * Each card reuses the surface's own `Photo`, `Money` and `StatusPill`
 * primitives, so a featured item with no photo is just its name and price — the
 * same no-photo layout the menu rows already ship, which is the normal V1
 * merchant. An available card is a link into the item; a sold-out one is inert,
 * the same rule the list rows follow.
 */
function HeroBand({
  items,
  availabilityOf,
}: {
  items: ChannelMenuItem[];
  availabilityOf: (itemId: string) => ItemAvailability | undefined;
}) {
  const {t, ln} = useI18n();
  return (
    <section className="q3-hero" aria-label={t('q3.featuredHeading')}>
      <h2 className="q3-hero-title">{t('q3.featuredHeading')}</h2>
      <ul className="q3-hero-list">
        {items.map((item) => (
          <li key={item.item_id} className="q3-hero-cell">
            <HeroCard
              item={item}
              name={ln(item.name)}
              available={availabilityOf(item.item_id)?.available ?? false}
              soldOutLabel={t('common.soldOut')}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

function HeroCard({
  item,
  name,
  available,
  soldOutLabel,
}: {
  item: ChannelMenuItem;
  name: string;
  available: boolean;
  soldOutLabel: string;
}) {
  const body = (
    <>
      {/* Nothing at all when `image_url` is null — a featured dish with no photo
          is name and price, the same as any menu row. */}
      <Photo src={item.image_url} shape="hero" className="q3-hero-photo" />
      <span className="q3-hero-name">{name}</span>
      {available ? null : (
        <span className="q3-hero-status">
          <StatusPill tone="warning">{soldOutLabel}</StatusPill>
        </span>
      )}
      <span className="q3-hero-prices">
        {item.regular_price_satang != null && (
          <Money satang={item.regular_price_satang} className="q3-row-was" />
        )}
        <Money satang={item.price_satang} className="q3-hero-price" />
      </span>
    </>
  );

  if (!available) return <div className="q3-hero-card is-soldout">{body}</div>;

  return (
    <Link className="q3-hero-card pokpok-tap" to={href(ROUTES.item, {itemId: item.item_id})}>
      {body}
    </Link>
  );
}

/**
 * The line under the title: what this price is, and which table it is going to.
 *
 * Two facts and one line, because they answer the same question — *is this the
 * menu I think it is?* The table label is `table_label` from the session (D-023,
 * on the wire since the contract gap was approved); when the venue has not set
 * one, the line is the price note alone rather than a `โต๊ะ —`.
 */
function MenuNote({hasMenu, tableLabel}: {hasMenu: boolean; tableLabel: string | null}) {
  const {t} = useI18n();
  if (!hasMenu) return null;
  return (
    <p className="pk-muted">
      {t('q3.channelPriceNote')}
      {tableLabel ? ` · ${t('common.table', {label: tableLabel})}` : null}
    </p>
  );
}

/*
 * THE STALENESS PILL IS GONE FROM THE MENU — 2026-08-14.
 *
 * It read `ข้อมูลอาจไม่ใช่ล่าสุด` ("this may not be up to date") whenever the
 * stream was not `open`, and its own docblock made the case against itself: *it
 * does not tell a guest to do anything, because there is nothing for them to
 * do.* That is a fair description of a message that should not be on the screen
 * where somebody is choosing lunch. It is a connection state — an operator's
 * concept — printed at a guest.
 *
 * What made it undeniable was the first real scan: `ฟ้าใส` is on a plan without
 * `guest_qr.order_pay`, the stream is refused outright, and the pill was
 * therefore permanent. A warning that is always on is not a warning; it is
 * furniture that makes a working menu look broken.
 *
 * **Nothing about honesty is lost, because nothing here was protecting a guest.**
 * A dish that sold out while the page was open is caught where it matters: the
 * cart re-derives `cart.issues` from the live projection on every render and
 * blocks the send, and the server refuses an unavailable line at submit
 * regardless of what any phone believed. Those are guards with consequences.
 * This was a caption.
 *
 * Staleness stays on Q14, where it is the opposite case: that screen exists to
 * report live kitchen progress, a frozen progress display *is* a lie, and the
 * guest can act on it by asking someone.
 */

/**
 * Loading.
 *
 * Grey bars rather than a spinner: they occupy the shape the menu will occupy, so
 * the first paint on a slow connection is already the right layout. Static, not
 * shimmering — an animation here costs frames on the phone least able to spare
 * them, and buys nothing. The bars are the same grey and the same shape as the
 * pre-bundle skeleton in `index.html`, because the guest has already been looking
 * at that one for a second and a half.
 *
 * The visible label is the announcement: `role="status"` on the container with
 * one line of real text says more than an `aria-label` on a stack of bars.
 */
function MenuSkeleton({label}: {label: string}) {
  return (
    <div className="q3-skeleton" role="status" aria-busy="true">
      <p className="pk-muted q3-skeleton-label">{label}</p>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <SkeletonRow key={i}>
          <SkeletonBar />
          <SkeletonBar width="64px" />
        </SkeletonRow>
      ))}
    </div>
  );
}
